import {
  type FirebaseApp,
  type FirebaseOptions,
  getApp,
  getApps,
  initializeApp,
} from 'firebase/app';

import { config } from '@/config';

let firebaseAppInstance: FirebaseApp | null = null;
const firebaseAppsByAuthDomain = new Map<string, FirebaseApp>();

function normalizeFirebaseAuthDomain(authDomain?: string | null) {
  const normalized = authDomain?.trim().toLowerCase();

  return normalized
    ? normalized.replace(/^https?:\/\//, '').replace(/\/+$/, '')
    : undefined;
}

function getOrInitializeFirebaseApp(
  options: FirebaseOptions,
  name: string
): FirebaseApp {
  if (getApps().some((app) => app.name === name)) {
    return getApp(name);
  }

  return initializeApp(options, name);
}

const getFirebaseApp = () => {
  if (!firebaseAppInstance) {
    firebaseAppInstance = getOrInitializeFirebaseApp(
      config.firebase satisfies FirebaseOptions,
      'mainFirebaseApp'
    );
  }

  return firebaseAppInstance;
};

export const mainFirebaseApp = getFirebaseApp();

export function getFirebaseAppForAuthDomain(
  authDomain?: string | null
): FirebaseApp {
  const normalizedAuthDomain = normalizeFirebaseAuthDomain(authDomain);
  const defaultAuthDomain = normalizeFirebaseAuthDomain(
    config.firebase.authDomain
  );

  if (!normalizedAuthDomain || normalizedAuthDomain === defaultAuthDomain) {
    return mainFirebaseApp;
  }

  const cachedApp = firebaseAppsByAuthDomain.get(normalizedAuthDomain);

  if (cachedApp) {
    return cachedApp;
  }

  /**
   * Some legacy enterprise SAML apps still return to the Firebase project's
   * firebaseapp.com handler even though new tenants use sso.gominerva.com.
   * Firebase stores redirect/popup state by auth-domain origin, so the client
   * app must start the flow on the same handler host the IdP returns to. The
   * Firebase project identifiers stay identical; only authDomain is swapped.
   */
  const firebaseApp = getOrInitializeFirebaseApp(
    {
      ...config.firebase,
      authDomain: normalizedAuthDomain,
    } satisfies FirebaseOptions,
    `authDomain:${normalizedAuthDomain}`
  );

  firebaseAppsByAuthDomain.set(normalizedAuthDomain, firebaseApp);

  return firebaseApp;
}
